import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, PageHeader, Btn, Field, inputCls, StatusPill, Empty, CheckboxGroup } from "../../components/dash/Kit";
import { ADMIN_STATUSES } from "../../data/site";
import { api, errMessage } from "../../lib/api";

const platforms = ["Instagram", "Facebook", "TikTok", "LinkedIn"];
const monthNow = new Date().toISOString().slice(0, 7);

export default function AdminContent() {
  const [params] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [userId, setUserId] = useState(params.get("user") || "");
  const [month, setMonth] = useState("");
  const [batch, setBatch] = useState({
    user_id: params.get("user") || "", month: monthNow, business_goal: "", promotion: "",
    campaign_theme: "", posts: 8, reels: 0, graphics: 0, platforms: ["Instagram"], instructions: "",
  });
  const [editing, setEditing] = useState(null);
  const [assets, setAssets] = useState([]);
  const [uploading, setUploading] = useState(false);
  const assetInput = useRef(null);

  const loadAssets = async (itemId) => {
    try {
      const { data } = await api.get(`/admin/assets`, { params: { content_item_id: itemId } });
      setAssets(data);
    } catch {
      setAssets([]);
    }
  };

  const openEditor = (item) => {
    setEditing(item);
    loadAssets(item.id);
  };

  const uploadFinishedPost = async (files) => {
    const file = files?.[0];
    if (!file || !editing) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error(`${file.name} is larger than 6MB.`);
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await api.post(`/content/${editing.id}/assets`, {
        filename: file.name, mime_type: file.type, size: file.size,
        data_url: dataUrl, note: "Finished post from PostMint",
      });
      toast.success("Post file uploaded and the customer was notified.");
      loadAssets(editing.id);
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setUploading(false);
    }
  };

  const loadItems = useCallback(() => {
    const query = {};
    if (userId) query.user_id = userId;
    if (month) query.month = month;
    api.get("/admin/content", { params: query }).then((r) => setItems(r.data)).catch(() => {});
  }, [userId, month]);

  useEffect(() => {
    api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadItems(); }, [loadItems]);

  const createBatch = async () => {
    if (!batch.user_id) {
      toast.error("Please choose a customer.");
      return;
    }
    try {
      const { data } = await api.post("/admin/batches", batch);
      toast.success(`Batch created with ${data.created_items} draft items.`);
      setUserId(batch.user_id);
      setMonth(batch.month);
      loadItems();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const saveItem = async () => {
    try {
      await api.put(`/admin/content/${editing.id}`, editing);
      toast.success("Content item saved.");
      setEditing(null);
      loadItems();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const publish = async (id) => {
    try {
      await api.post(`/admin/content/${id}/publish`);
      toast.success("Customer notified — item is ready for review.");
      loadItems();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-content-page">
      <PageHeader title="Content Orders" body="Create a monthly content batch, then fill in and release each item." />

      <Card testId="admin-batch-form">
        <p className="font-display text-lg font-semibold text-slate-900">Create monthly content batch</p>
        <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Customer">
            <select data-testid="batch-customer-select" className={inputCls} value={batch.user_id} onChange={(e) => setBatch((b) => ({ ...b, user_id: e.target.value }))}>
              <option value="">Choose a customer</option>
              {customers.map((c) => (<option key={c.id} value={c.id}>{c.business_name}</option>))}
            </select>
          </Field>
          <Field label="Month" htmlFor="batch-month">
            <input id="batch-month" type="month" data-testid="batch-month-input" className={inputCls} value={batch.month} onChange={(e) => setBatch((b) => ({ ...b, month: e.target.value }))} />
          </Field>
          <Field label="Business Goal" htmlFor="batch-goal">
            <input id="batch-goal" data-testid="batch-goal-input" className={inputCls} value={batch.business_goal} onChange={(e) => setBatch((b) => ({ ...b, business_goal: e.target.value }))} />
          </Field>
          <Field label="Promotion" htmlFor="batch-promo">
            <input id="batch-promo" data-testid="batch-promotion-input" className={inputCls} value={batch.promotion} onChange={(e) => setBatch((b) => ({ ...b, promotion: e.target.value }))} />
          </Field>
          <Field label="Campaign Theme" htmlFor="batch-theme">
            <input id="batch-theme" data-testid="batch-theme-input" className={inputCls} value={batch.campaign_theme} onChange={(e) => setBatch((b) => ({ ...b, campaign_theme: e.target.value }))} />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            {[["posts", "Posts"], ["reels", "Reels"], ["graphics", "Graphics"]].map(([k, l]) => (
              <Field key={k} label={l} htmlFor={`batch-${k}`}>
                <input id={`batch-${k}`} type="number" min="0" data-testid={`batch-${k}-input`} className={inputCls} value={batch[k]} onChange={(e) => setBatch((b) => ({ ...b, [k]: Number(e.target.value) }))} />
              </Field>
            ))}
          </div>
        </div>
        <div className="mt-5">
          <Field label="Platforms">
            <CheckboxGroup name="batch-platforms" columns={4} options={platforms} value={batch.platforms} onChange={(v) => setBatch((b) => ({ ...b, platforms: v }))} />
          </Field>
        </div>
        <div className="mt-5">
          <Field label="Special Instructions" htmlFor="batch-instructions">
            <textarea id="batch-instructions" rows={3} data-testid="batch-instructions-input" className={inputCls} value={batch.instructions} onChange={(e) => setBatch((b) => ({ ...b, instructions: e.target.value }))} />
          </Field>
        </div>
        <Btn testId="create-batch-button" className="mt-6" onClick={createBatch}>Create Content Batch</Btn>
      </Card>

      <Card testId="admin-content-filters">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Filter by customer">
            <select data-testid="content-filter-customer" className={inputCls} value={userId} onChange={(e) => setUserId(e.target.value)}>
              <option value="">All customers</option>
              {customers.map((c) => (<option key={c.id} value={c.id}>{c.business_name}</option>))}
            </select>
          </Field>
          <Field label="Filter by month" htmlFor="content-filter-month">
            <input id="content-filter-month" type="month" data-testid="content-filter-month" className={inputCls} value={month} onChange={(e) => setMonth(e.target.value)} />
          </Field>
        </div>
      </Card>

      {items.length === 0 ? (
        <Empty testId="admin-content-empty" title="No content items" body="Create a batch above to generate draft items." />
      ) : (
        <div className="space-y-3" data-testid="admin-content-list">
          {items.map((i) => (
            <Card key={i.id} testId={`admin-content-item-${i.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    {i.post_date} · {i.platform} · {i.content_type} · {i.title}
                  </p>
                  <p className="mt-1 line-clamp-2 text-sm text-slate-600">{i.caption || "No caption yet"}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusPill status={i.status} />
                  <Btn variant="outline" testId={`admin-edit-item-${i.id}`} onClick={() => openEditor(i)}>Edit</Btn>
                  <Btn testId={`admin-publish-item-${i.id}`} onClick={() => publish(i.id)}>Release to customer</Btn>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/40 p-4" role="dialog" aria-label="Edit content item">
          <div className="mx-auto my-8 w-full max-w-2xl rounded-3xl bg-white p-6" data-testid="admin-edit-modal">
            <p className="font-display text-xl font-bold text-slate-900">Edit content item</p>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <Field label="Post Date" htmlFor="edit-date">
                <input id="edit-date" type="date" data-testid="edit-date-input" className={inputCls} value={editing.post_date || ""} onChange={(e) => setEditing((v) => ({ ...v, post_date: e.target.value }))} />
              </Field>
              <Field label="Platform">
                <select data-testid="edit-platform-select" className={inputCls} value={editing.platform || ""} onChange={(e) => setEditing((v) => ({ ...v, platform: e.target.value }))}>
                  {platforms.map((p) => (<option key={p} value={p}>{p}</option>))}
                </select>
              </Field>
              <Field label="Post Type">
                <select data-testid="edit-type-select" className={inputCls} value={editing.content_type || ""} onChange={(e) => setEditing((v) => ({ ...v, content_type: e.target.value }))}>
                  {["Post", "Story", "Reel", "TikTok", "Graphic", "Promotion"].map((t) => (<option key={t} value={t}>{t}</option>))}
                </select>
              </Field>
              <Field label="Status">
                <select data-testid="edit-status-select" className={inputCls} value={editing.status || "Draft"} onChange={(e) => setEditing((v) => ({ ...v, status: e.target.value }))}>
                  {ADMIN_STATUSES.map((t) => (<option key={t} value={t}>{t}</option>))}
                </select>
              </Field>
            </div>

            {[
              ["title", "Title", 1],
              ["caption", "Caption", 5],
              ["hashtags", "Hashtags", 2],
              ["cta", "Call To Action", 1],
              ["reel_concept", "Reel Concept", 4],
              ["graphic_note", "Graphic notes", 2],
              ["image_url", "Graphic / image URL", 1],
              ["internal_notes", "Internal Notes", 2],
            ].map(([k, l, rows]) => (
              <div key={k} className="mt-4">
                <Field label={l} htmlFor={`edit-${k}`}>
                  {rows > 1 ? (
                    <textarea id={`edit-${k}`} rows={rows} data-testid={`edit-${k}-input`} className={inputCls} value={editing[k] || ""} onChange={(e) => setEditing((v) => ({ ...v, [k]: e.target.value }))} />
                  ) : (
                    <input id={`edit-${k}`} data-testid={`edit-${k}-input`} className={inputCls} value={editing[k] || ""} onChange={(e) => setEditing((v) => ({ ...v, [k]: e.target.value }))} />
                  )}
                </Field>
              </div>
            ))}

            <div className="mt-5 rounded-2xl border border-slate-200 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Finished post files</p>
              {assets.length === 0 ? (
                <p className="mt-1.5 text-sm text-slate-500">
                  Nothing uploaded yet. Upload the finished graphic or video — the customer is notified and can download it.
                </p>
              ) : (
                <ul className="mt-2 space-y-1.5">
                  {assets.map((a) => (
                    <li key={a.id} className="text-sm text-slate-700">
                      {a.filename}
                      <span className="ml-2 text-xs text-slate-400">
                        {a.uploaded_by === "admin" ? "PostMint" : "customer upload"} · {(a.size / 1024).toFixed(0)} KB
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <input
                ref={assetInput}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,.mp4,.mov,.pdf"
                className="hidden"
                data-testid="admin-asset-file-input"
                onChange={(e) => uploadFinishedPost(e.target.files)}
              />
              <Btn variant="outline" testId="admin-upload-post-button" className="mt-3" disabled={uploading} onClick={() => assetInput.current?.click()}>
                {uploading ? "Uploading…" : "Upload finished post"}
              </Btn>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <Btn testId="admin-save-item-button" onClick={saveItem}>Save Item</Btn>
              <Btn variant="outline" testId="admin-cancel-edit-button" onClick={() => setEditing(null)}>Cancel</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
